import { clearJwt, readJwt } from "../auth/storage";

/**
 * Typed fetch wrapper (frontend/CLAUDE.md: "all fetch calls go through
 * src/api/client.ts").
 *
 * - Auto-injects the `Authorization: Bearer <jwt>` header when present
 * - On 401, clears the stored JWT and triggers the registered unauthorized
 *   handler (the AuthContext registers a router push to `/login`)
 * - JSON-encodes the request body when supplied as an object
 * - Returns the parsed JSON body when the response is JSON, otherwise null
 *
 * On non-2xx responses other than 401, throws {@link ApiError} carrying
 * the status code and the parsed error body, so pages can surface
 * server messages without re-parsing.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function registerUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** When true, do not attach the JWT (used by /api/auth/* endpoints). */
  anonymous?: boolean;
}

const JSON_CT = "application/json";

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: JSON_CT,
    ...(opts.headers ?? {}),
  };
  let body: BodyInit | undefined;
  if (opts.body !== undefined && opts.body !== null) {
    headers["Content-Type"] = JSON_CT;
    body = typeof opts.body === "string" ? opts.body : JSON.stringify(opts.body);
  }
  if (!opts.anonymous) {
    const jwt = readJwt();
    if (jwt) headers["Authorization"] = `Bearer ${jwt}`;
  }

  const res = await fetch(path, {
    method: opts.method ?? "GET",
    headers,
    body,
    signal: opts.signal,
  });

  if (res.status === 401) {
    // Only a *session* 401 — a call that carried the JWT — means the token is
    // dead and should trigger the global logout/redirect. Anonymous calls are
    // the /api/auth/** login flow, which legitimately returns 401 for a wrong or
    // expired OTP; that is an inline form error, not a dead session, and must not
    // clear state or bounce the user back to /login.
    if (!opts.anonymous) {
      clearJwt();
      unauthorizedHandler?.();
    }
    throw new ApiError(401, await safeJson(res), "unauthorized");
  }

  if (res.status === 204 || res.headers.get("content-length") === "0") {
    if (!res.ok) throw new ApiError(res.status, null);
    return null as T;
  }

  const ct = res.headers.get("content-type") ?? "";
  const payload = ct.includes("json") ? await safeJson(res) : await res.text();

  if (!res.ok) {
    throw new ApiError(res.status, payload);
  }
  return payload as T;
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

// ------------------------------------------------------------
// Typed endpoint wrappers — the only places API paths are
// hardcoded. Pages should not assemble URLs themselves.
// ------------------------------------------------------------

export interface HealthResponse {
  status: string;
  mode: string;
  version: string;
  /** Backend git commit — added by deferred ops chore; may be absent. */
  commit?: string;
}

export interface JwtResponse {
  token: string;
  expiresInSeconds: number;
}

export interface CycleResponse {
  id: number;
  /** Null for cycles read from the permanent analytics record (S56d) — that
   *  store has no cycle_uid; the UI renders a `#<id>` fallback. */
  cycleUid: string | null;
  bias: "BUY" | "SELL";
  mode: "DEMO" | "LIVE";
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
  riskBudgetEur: number;
  /** Nullable: only populated once the cycle has settled. */
  realizedPnlEur: number | null;
  /**
   * Qualifies {@link realizedPnlEur}: true → every closed position
   * contributed a known PnL; false → at least one position lacked
   * data to compute its PnL and was skipped from the sum (render as
   * partial). Null on still-running or pre-aggregation legacy cycles.
   */
  pnlComplete: boolean | null;
  strategyVersion: string;
  // ── PROTECTED_LADDER_V6 live two-trail state (V18). Optional: absent on a
  //    pre-V6-API backend, null on history/analytics rows (H2-only state). ──
  /** Shared swing reference (M1), re-centred each action — R/D measure from these. */
  swingHi?: number | null;
  swingLo?: number | null;
  /** Per-side trail armed (M3: count==n AND latent ≥ n·r_trail at some prior tick). */
  buyTrailArmed?: boolean | null;
  sellTrailArmed?: boolean | null;
  /** Per-side trail extreme once armed (stop = extreme ∓ r_trail). */
  /** Origine du biais ("AUTO_WAITSHORT" = valeur historique — jambe retirée 2026-09). */
  biasSource?: string | null;
  /** Extrême à l'armement par côté (V22, escalier) — null avant armement. */
  buyArmExtreme?: number | null;
  sellArmExtreme?: number | null;
  buyTrailExtreme?: number | null;
  sellTrailExtreme?: number | null;
  /** Jambes fermées par l'opérateur (2026-11-02) — plafond AUTO = maxSide − n ; absent sur l'historique. */
  operatorClosedCount?: number | null;
  /**
   * Canonical §7 worst-case loss of the cycle's OPEN positions — the SAME profit-aware value the
   * backend RiskManager gate enforces (a stop trailing in profit contributes 0). Populated by
   * GET /current for the live cycle; null on history/analytics rows. Drive
   * "budget used %" off THIS, never a |entry − stop| sum (which climbs as profit is locked).
   */
  worstCaseLossEur?: number | null;
  /**
   * Forward-looking two-trail trigger prices (the inverse of the mechanic's per-tick conditions) + the
   * frozen §2 params, so the cockpit can show "next action, at what price" without re-implementing the
   * mechanic. Populated by GET /current for the live cycle; absent/null otherwise. See {@link LadderProjection}.
   */
  ladderProjection?: LadderProjection | null;
  /**
   * Read-only echo of the frozen operating params the cockpit displays (financing, entry-quote guards, LIVE
   * session window). Populated by GET /current for the live cycle; absent/null otherwise.
   */
  cockpitContext?: CockpitContext | null;
  /**
   * Override opérateur « TENIR LE WEEK-END » (2026-10-13) en vigueur sur ce cycle : le flatten de la
   * prochaine coupure calendaire est sauté, les stops garantis restent chez IG. Null = défaut (flatten).
   */
  holdOverClose?: HoldOverClose | null;
}

export interface HoldOverClose {
  grantedBy: string;
  grantedAt: string;
  untilClose: string;
}

/** Frozen operating params echoed for the cockpit (financing, entry guards, session window). Read-only. */
export interface CockpitContext {
  /** §3.2 overnight funding on/off; rate is a % of leg notional (entry·size) per night. */
  financingEnabled: boolean;
  financingBaseRatePct: number;
  /** Entry-quote guards (§19): an entry is skipped when spread or quote age exceeds these. */
  maxSpreadPoints: number;
  maxQuoteAgeMs: number;
  /** LIVE session filter: Paris hours during which entries are blocked, + DEMO/LIVE enforcement. */
  blockedHoursParisLive: number[];
  sessionEnforcementDemo: string;
  sessionEnforcementLive: string;
}

/**
 * Forward-looking two-trail projections derived from the live ladder state (backend `LadderProjection`).
 * Prices are null when not applicable: a trigger whose side is full, an arm price for a side below
 * `trailArmCount` (schema 14 — decoupled from `maxSide`) or already armed, or any swing-based trigger
 * on a pre-V6 row with no swing.
 */
export interface LadderProjection {
  /** Next add on the BIAS side (dip-bounce). Null when the bias side is full. */
  reinforceTriggerPrice: number | null;
  /** Price at which the BUY side's trail arms (price must reach ≥ this). Null below trailArmCount or armed. */
  buyArmPrice: number | null;
  /** Price at which the SELL side's trail arms (price must reach ≤ this). Null below trailArmCount or armed. */
  sellArmPrice: number | null;
  /** Frozen §2 params, echoed so the client can label + compute mid-relative distances / progress. */
  maxSide: number;
  /** Effective M3 arming threshold (schema 14): the trail can arm from this many positions. */
  trailArmCount: number;
  /** Whether an armed side keeps reinforcing up to maxSide (TRAIL_ARM_X "+open"). */
  openAfterArm: boolean;
  slPoints: number;
  reinforceThresholdPoints: number;
  /** Schema 15 — escalier de trail : rt se resserre à trailPoints après advancePoints d'avance
   *  post-armement ; 0/0 (ou absents, payload pré-CH10) = OFF. */
  trailStairAdvancePoints?: number | null;
  trailStairTrailPoints?: number | null;
  trailDistancePoints: number;
  costPointsPerPosition: number;
}

export interface SystemStateResponse {
  state: "HEALTHY" | "DEGRADED" | "SAFE_SUSPENDED" | "SCALING_REVIEW";
  botMode: "DEMO" | "LIVE" | "REPLAY";
  igApiMode: "DEMO" | "LIVE";
  /** F3: whether the manual emergency stop (kill-switch) is engaged. */
  manualEmergencyStop?: boolean;
}

// --- F5: Runtime parameter apply/rollback result (S56a, DEMO/REPLAY only) ---
export interface ParameterResult {
  status: string;
  activeVersionId: number | null;
  previousVersionId: number | null;
  paramsHash: string | null;
  cycleStartAllowed: boolean;
  reason: string | null;
}

// --- S8: read-only parameter version list + A/B (front-office, study Q6) ---
export interface ParameterVersion {
  id: number;
  status: string;
  source: string;
  parentId: number | null;
  paramsHash: string;
  strategyVersion: string;
  codeCommit: string;
  createdBy: string;
  createdAt: string;
  activatedAt: string | null;
  reasoning: string | null;
  activePointer: boolean;
  /**
   * True iff the current parameter codec can read this version. A false value (an older
   * schema_version, e.g. a pre-V6 set) cannot be activated — the backend refuses it — so the
   * UI greys out the Activate action instead of letting it brick cycle-start.
   */
  schemaCompatible: boolean;
}

export interface ParameterVersionList {
  activePointerId: number | null;
  versions: ParameterVersion[];
}

/**
 * B2 — GET /api/parameters/{id}: one complete version. {@link paramsJson} is the canonical
 * schema_version-2 document verbatim (the exact string paramsHash was computed from) — parse it
 * client-side to show/diff values.
 */
export interface ParameterVersionDetail extends ParameterVersion {
  paramsJson: string;
}

/**
 * Tier1Metrics as serialised by the backend (SPEC §22 replay metrics). Decimal fields arrive as JSON
 * numbers; {@code profitFactor} is canonicalised to a string ("inf" when no losses, "0" when no gains).
 */
export interface Tier1Metrics {
  cycleCount: number;
  positionCount: number;
  winCount: number;
  lossCount: number;
  winRate: number | string | null;
  totalPnlEur: number | string | null;
  maxDrawdownEur: number | string | null;
  profitFactor: string;
  maxWorstCaseLossEur: number | string | null;
  wclBreaches: number;
  stopWideningBlocked: number;
  maxOpenPositions: number;
  skipDistribution: Record<string, number>;
  circuitBreakerTriggers: Record<string, number>;
  pnlByRole: Record<string, number | string>;
  exitReasonDistribution: Record<string, number>;
  pnlByDirection: Record<string, number | string>;
  pnlBySession: Record<string, number | string>;
  trailingActivations: number;
  maxFavorableExcursionEur: number | string | null;
  maxAdverseExcursionEur: number | string | null;
  runtimeMs: number;
}


export type EnvironmentType = "DEMO" | "LIVE" | "REPLAY";

/**
 * Read-only environment descriptor from {@code GET /api/mode/status} (S56a).
 * DEMO and LIVE are separate dedicated deployments — there is no runtime mode
 * switch, so this is purely informational. {@code accountId} is the configured
 * bot account ({@code ZSKS6}/{@code BWQPJ}), or {@code null} in REPLAY.
 */
export interface ModeStatusResponse {
  botMode: "DEMO" | "LIVE" | "REPLAY";
  igApiMode: "DEMO" | "LIVE";
  environmentType: EnvironmentType;
  modeConsistent: boolean;
  capabilities: {
    liveFireCapable: boolean;
    placesRealOrders: boolean;
    readOnly: boolean;
    runtimeParameterApply: boolean;
  };
  banner: { severity: "info" | "warning" | "danger"; text: string };
  accountId: string | null;
}

export interface PositionResponse {
  id: number;
  cycleId: number;
  dealId: string;
  direction: "BUY" | "SELL";
  role: "INITIALE" | "RENFORT" | "EXTENSION" | "DEFENSE" | "OPERATEUR";
  protectedState: "INITIAL" | "BREAK_EVEN" | "BREAK_EVEN_PLUS" | "TRAILING";
  sizePerPointEur: number;
  entryPrice: number;
  stopPrice: number;
  openedAt: string;
  closedAt: string | null;
  closePrice: number | null;
  realizedPnlEur: number | null;
  exitReason: string | null;
  /**
   * SPEC §12 / §14 / S52b — factual detection channel: how the bot
   * learned the position closed. Disjoint from {@link exitReason}.
   * Rendered as the hover / detail-drawer secondary label.
   */
  closeSource: string | null;
}

export type PnlSign = "POS" | "NEG" | "FLAT";

// ── Entrée opérateur « Acheter maintenant » (2026-10-22) ──

export type OperatorEntryMode = "IN_CYCLE" | "NEW_CYCLE" | "UNAVAILABLE";

export type OperatorEntryUnavailableReason =
  | "AUTO_NOT_ARMED"
  | "INITIALE_PENDING"
  | "CYCLE_FLAT"
  | "STREAM_NOT_STARTED"
  | "NO_OPEN_WINDOW"
  | "TRAIL_ARMED"
  | "NO_QUOTE";

export type OperatorEntryRequestStatus =
  | "PENDING"
  | "CONSUMED"
  | "FILLED"
  | "REFUSED"
  | "EXPIRED"
  | "CANCELLED";

export interface OperatorEntryPreview {
  mode: "IN_CYCLE" | "NEW_CYCLE";
  direction: "BUY" | "SELL";
  mid: number;
  sizePerPointEur: number;
  stopDistancePoints: number;
  worstCaseBeforeEur: number;
  worstCaseAfterEur: number;
  riskBudgetEur: number;
  slotsUsed: number;
  maxSide: number;
}

export interface OperatorEntryRequest {
  cycleId: number;
  requestedBy: string;
  requestedAt: string;
  expiresAt: string;
  status: OperatorEntryRequestStatus;
  reason: string | null;
  dealId: string | null;
  resolvedAt: string | null;
}

/** Fermeture opérateur « Fermer cette jambe » (2026-11-02) — demande posée, consommée au tick suivant. */
export type OperatorCloseRequestStatus = OperatorEntryRequestStatus;

export interface OperatorCloseRequest {
  cycleId: number;
  dealId: string;
  requestedBy: string;
  requestedAt: string;
  expiresAt: string;
  status: OperatorCloseRequestStatus;
  reason: string | null;
  resolvedAt: string | null;
}

export interface OperatorCloseStatus {
  request: OperatorCloseRequest | null;
}

export interface OperatorEntryStatus {
  armed: boolean;
  mode: OperatorEntryMode;
  unavailableReason: OperatorEntryUnavailableReason | null;
  preview: OperatorEntryPreview | null;
  request: OperatorEntryRequest | null;
}

export interface CycleHistoryFilters {
  bias?: "BUY" | "SELL";
  endReason?: string;
  pnlSign?: PnlSign;
  /** ISO-8601 instant; inclusive lower bound on startedAt. */
  from?: string;
  /** ISO-8601 instant; inclusive upper bound on startedAt. */
  to?: string;
}

export interface CycleHistorySummary {
  totalCycles: number;
  settledCycles: number;
  winCount: number;
  lossCount: number;
  winRate: number | null;
  avgPnlEur: number | null;
  bestPnlEur: number | null;
  worstPnlEur: number | null;
  totalPnlEur: number | null;
}

export interface CycleHistoryPage {
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  content: CycleResponse[];
  summary: CycleHistorySummary;
}

export type LogLevel = "TRACE" | "DEBUG" | "INFO" | "WARN" | "ERROR";

export interface LogLineDto {
  timeMs: number;
  level: LogLevel;
  logger: string;
  thread: string;
  message: string;
}

export interface LogsQuery {
  level?: LogLevel;
  component?: string;
  search?: string;
  since?: number;
  limit?: number;
}

export interface LogsResponse {
  lines: LogLineDto[];
  lastTimeMs: number;
  bufferSize: number;
  bufferCapacity: number;
}

export interface SettingsResponse {
  mode: { botMode: string; igApiMode: string };
  twoTrail: {
    slPoints: number;
    reinforceThresholdPoints: number;
    trailDistancePoints: number;
    maxSide: number;
    /** Schema 14 — RAW arming threshold: 0 = suit maxSide (couplé), sinon 1..maxSide. */
    trailArmCount: number;
    /** Schema 14 — le seuil que le moteur arme réellement (0 résolu en maxSide, borné). */
    effectiveTrailArmCount: number;
    /** Schema 14 — un côté armé continue de se renforcer jusqu'à maxSide (« +open »). */
    openAfterArm: boolean;
    /** Schema 15 (CH10) — TRAIL-STAIR brut : rt se resserre à trailPoints après advancePoints
     *  d'avance post-armement ; 0/0 = OFF ; actif seulement si 0 < trail < rt. Optionnels
     *  (payload pré-schema-15 → rendre « ? »). */
    trailStairAdvancePoints?: number;
    trailStairTrailPoints?: number;
    trailStairActive?: boolean;
  };
  /** Schema 16 — knobs de la chaîne AUTO (valeurs EFFECTIVES ; versionnés dans le bundle).
   *  Optionnel : payload pré-16. */
  auto?: {
    lossThresholdPips: number;
    consecutiveLossLimit: number;
    noOpenBeforeCloseMinutes: number;
    resumePullbackPts: number;
    winPullbackPts: number;
  };
  positionLimits: {
    maxTotalHard: number;
    consecutiveStopLimit: number;
  };
  stream: { maxSpreadPoints: number };
  sizing: {
    pointValueEur: number;
    positionSizeMaxEurPerPoint: number;
  };
  liveSafety: {
    cycleRiskPct: number;
    dailyLossLimitMultiple: number;
    scalingReviewThresholdPct: number;
  };
  /** V20 — flat-when-closed master switch + pre-close flatten lead + financing (effective values).
   *  Optional: a pre-V20 backend payload omits it (render "?" rather than inventing values). */
  policy?: {
    flatWhenClosed: boolean;
    flattenLeadMinutes: number;
    financingEnabled: boolean;
    financingBaseRatePct: number;
  };
}

/** V20 — market calendar (weekly schedule + exception days + derived live facts). */
export interface CalendarWeekly {
  anchorTz: string;
  weekOpenDow: string;
  weekOpenTime: string;
  weekCloseDow: string;
  weekCloseTime: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface CalendarExceptionDay {
  date: string;
  type: "CLOSED" | "EARLY_CLOSE" | "MODIFIED";
  openLocal: string | null;
  closeLocal: string | null;
  reopenLocal: string | null;
  label: string;
  source: string;
  note: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface CalendarResponse {
  weekly: CalendarWeekly | null;
  exceptions: CalendarExceptionDay[];
  derived: {
    open: boolean;
    closedReason: string | null;
    nextCloseUtc: string | null;
    nextOpenUtc: string | null;
    /** null when flatWhenClosed is OFF (§25 — never show a time that won't happen). */
    flattenAtUtc: string | null;
    flatWhenClosed: boolean;
    flattenLeadMinutes: number;
  };
}

export type AnalyticsSource = "BOT_DEMO" | "BOT_LIVE";

// ── /api/analytics/overview (2026-07-09 Analytics redesign — one coherent fetch) ──

export interface OverviewEquityPoint {
  /** ISO instant of the cycle end. */
  t: string;
  cycleId: number;
  pnlEur: number;
  cumPnlEur: number;
}

export interface OverviewDailyPnl {
  /** Paris trading day, YYYY-MM-DD. */
  date: string;
  pnlEur: number;
  cycles: number;
}

export interface OverviewKpis {
  /** Settled-in-window + currently running. */
  cycles: number;
  settled: number;
  wins: number;
  losses: number;
  netPnlEur: number;
  avgPnlEur: number | null;
  bestPnlEur: number | null;
  worstPnlEur: number | null;
  winRatePct: number | null;
  /** Gross wins / gross losses; null when no losing cycle yet. */
  profitFactor: number | null;
  maxDrawdownEur: number;
}

export interface OverviewCycleRow {
  id: number;
  bias: "BUY" | "SELL";
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
  pnlEur: number | null;
  riskBudgetEur: number | null;
  maxWclEur: number | null;
  versionId: number | null;
  positions: number;
  renforts: number;
}

export interface OverviewRoleStat {
  role: string;
  positions: number;
  wins: number;
  pnlEur: number;
}

export interface OverviewExitStat {
  exitReason: string;
  positions: number;
  pnlEur: number;
}

export interface OverviewVersionStat {
  versionId: number | null;
  /** Human param label derived from the frozen parameters JSONB (e.g.
   *  "r150 · d600/600 · t150 · n4 · risk 80%"); null → render v{versionId}. */
  label: string | null;
  cycles: number;
  settled: number;
  wins: number;
  netPnlEur: number;
  winRatePct: number | null;
  maxWclEur: number | null;
  firstCycleAt: string | null;
  lastCycleAt: string | null;
}

export interface OverviewSkipStat {
  decisionType: string;
  count: number;
}

export interface AnalyticsOverviewResponse {
  source: string;
  windowDays: number;
  kpis: OverviewKpis;
  equity: OverviewEquityPoint[];
  daily: OverviewDailyPnl[];
  cycles: OverviewCycleRow[];
  byRole: OverviewRoleStat[];
  byExit: OverviewExitStat[];
  byVersion: OverviewVersionStat[];
  skips: OverviewSkipStat[];
}

export interface ParamChangeRow {
  id: number;
  changedAt: string;
  paramName: string;
  oldValue: string;
  newValue: string;
  source: string;
  /** Null unless the change was driven by a Claude suggestion (dormant producer). */
  aiSuggestionId: number | null;
  /** Null when no reasoning was recorded. */
  reasoning: string | null;
}

export interface DecisionQualityTypeBreakdown {
  decisionType: string;
  totalCount: number;
  inProfitableCycles: number;
  inLosingCycles: number;
  inUnsettledCycles: number;
}

export interface DecisionQualityResponse {
  source: AnalyticsSource;
  totalDecisions: number;
  byType: DecisionQualityTypeBreakdown[];
}

export interface RiskStatsPercentiles {
  sampleSize: number;
  mean: number | null;
  p50: number | null;
  p90: number | null;
  p99: number | null;
  max: number | null;
}

export interface RiskStatsDirectional {
  fullyDirectional: number;
  mixed: number;
  empty: number;
}

export interface RiskStatsResponse {
  source: AnalyticsSource;
  settledCycles: number;
  worstCaseLossEur: RiskStatsPercentiles;
  directional: RiskStatsDirectional;
}

export interface StreamEventCount {
  eventType: string;
  count: number;
}

export interface QuoteAgeStats {
  sampleSize: number;
  p50: number | null;
  p90: number | null;
  p99: number | null;
  max: number | null;
}

export interface StreamQualityResponse {
  windowHours: number;
  byEventType: StreamEventCount[];
  quoteAgeMs: QuoteAgeStats;
}

// --- Daily P&L review (S52) ---
export interface DailyReviewLossLimit {
  /** True only in LIVE (DEMO is advisory). */
  enforced: boolean;
  limitEur: number | null;
  /** Signed realized P&L for the day; a loss is negative. */
  usedEur: number | null;
  remainingEur: number | null;
  breached: boolean;
}

/**
 * Deterministic daily summary. Numeric fields are {@code null} when the
 * value is genuinely unknown (no settled cycle, no mark price, analytics
 * store unavailable) — the UI must render that as "—"/"unknown", never as
 * a fabricated {@code +€0.00}.
 */
export interface DailyReviewResponse {
  parisDate: string;
  mode: "DEMO" | "LIVE";
  cycleCount: number;
  wins: number;
  losses: number;
  realizedPnlEur: number | null;
  pnlComplete: boolean;
  unrealizedPnlEur: number | null;
  lossLimit: DailyReviewLossLimit | null;
  circuitBreakerEvents: number | null;
  streamQuality: StreamQualityResponse | null;
  warnings: string[];
}

/**
 * Stop-movement row returned by GET /api/cycles/{id}/stop-movements
 * (read-only pre-S52 UI cockpit endpoint). Rejected widening attempts
 * keep accepted=false + skipReason=SKIPPED_WIDENING_FORBIDDEN
 * (SPEC §19.5).
 */
export interface StopMovementResponse {
  id: number;
  positionId: number;
  proposedAt: string;
  oldStop: number | null;
  newStop: number;
  kind: string;
  accepted: boolean;
  skipReason: string | null;
}


/** The losing cycle that disarmed AUTO (GET /api/auto/status → lastTrip). */
export interface AutoTrip {
  cycleId: number;
  cycleUid: string;
  pnlEur: number;
  endReason: string;
  at: string;
}

/** Statut AUTO (GET /api/auto/status) — modèle pause/reprise 2026-07-19. armed = le
 *  master switch OTP ; paused = surcouche « en pause jusqu'à la réouverture »
 *  (pauseReason: CONSECUTIVE_LOSSES | FLATTEN_CLOSE ; resumeAt = réouverture calculée
 *  à la pause, null si le calendrier était muet). lossThresholdPips = dead-band en
 *  POINTS (le niveau € = × la taille par position du cycle). */
export interface AutoStatus {
  armed: boolean;
  paused: boolean;
  pauseReason: "CONSECUTIVE_LOSSES" | "FLATTEN_CLOSE" | "WIN_PULLBACK" | null;
  resumeAt: string | null;
  consecutiveLosses: number;
  lossThresholdPips: number;
  consecutiveLossLimit: number;
  noOpenBeforeCloseMinutes: number;
  /** Reprise au pull-back (pts) des pauses pertes-consécutives — 0 = à la réouverture seule. */
  resumePullbackPts: number;
  /** WINPB — attente post-gain (pts) : repli sous la sortie gagnante avant le cycle suivant ; 0 = off. */
  winPullbackPts: number;
  lastTrip: AutoTrip | null;
}

/** Dérogation ponctuelle du plafond spread de CIRCUIT_STREAM_HEALTH (owner 2026-09-02) —
 *  GET /api/breakers/stream-health/status. active = dérogation en cours (le check spread
 *  est assoupli, les 4 autres checks restent actifs) ; se consomme seule à la prochaine
 *  ouverture bot réussie, ou expire après 2 h si aucune ouverture ne survient. */
export interface StreamHealthOverrideStatus {
  active: boolean;
  activeSince: string | null;
}

/** Position du cockpit MANUEL (dual-cockpit 2026-07-30) — table manual_position, H2.
 *  origin = RANGE (tir de l'automate de bande) ou MANUAL (lignes historiques du
 *  ticket d'ordre supprimé le 2026-09-01 — plus aucun producteur). */
export interface ManualPositionView {
  dealId: string;
  accountId: string;
  direction: "BUY" | "SELL";
  origin: "MANUAL" | "RANGE";
  sizePerPointEur: number;
  entryPrice: number;
  stopPrice: number;
  openedAt: string;
  closedAt: string | null;
  closePrice: number | null;
  closeReason: "USER_CLOSE" | "BROKER_CLOSE" | "RANGE_FLIP" | null;
}

/** Mode range du cockpit MANUEL (owner 2026-07-31) : SELL au plafond, BUY au
 *  plancher, à l'intérieur de la fenêtre armée. sellArmed/buyArmed = hystérésis
 *  (un tir par touche de bord) ; lastEvent = dernier geste ou refus, en clair. */
export interface ManualRangeView {
  active: boolean;
  floorLevel: number | null;
  ceilingLevel: number | null;
  sizePerPointEur: number | null;
  stopDistancePoints: number | null;
  sellArmed: boolean;
  buyArmed: boolean;
  lastFireAt: string | null;
  triggerCount: number;
  lastEvent: string | null;
}

/** Statut du cockpit MANUEL (GET /api/manual/status). armed = fenêtre OTP 12 h ;
 *  sharedWithBot = vrai en DEMO (ZSKS6 partagé, pas de 2e compte démo) ;
 *  pendingAdoption = un OPEN confirm-failed attend son adoption (§12). */
export interface ManualStatus {
  enabled: boolean;
  armed: boolean;
  armedUntil: string | null;
  accountId: string;
  sharedWithBot: boolean;
  pendingAdoption: boolean;
  positions: ManualPositionView[];
  range: ManualRangeView;
}

export const api = {
  health: () => apiRequest<HealthResponse>("/api/health", { anonymous: true }),

  requestCode: (email: string) =>
    apiRequest<void>("/api/auth/request-code", {
      method: "POST",
      body: { email },
      anonymous: true,
    }),

  verifyCode: (email: string, code: string) =>
    apiRequest<JwtResponse>("/api/auth/verify-code", {
      method: "POST",
      body: { email, code },
      anonymous: true,
    }),

  startCycle: (bias: "BUY" | "SELL") =>
    apiRequest<CycleResponse>("/api/cycles/start", {
      method: "POST",
      body: { bias },
    }),

  stopCycle: () =>
    apiRequest<CycleResponse>("/api/cycles/stop", { method: "POST" }),

  /** GET /api/cycles/current — null when no active cycle (HTTP 204). */
  currentCycle: () => apiRequest<CycleResponse | null>("/api/cycles/current"),

  systemState: () => apiRequest<SystemStateResponse>("/api/system/state"),

  /** F3: POST /api/system/emergency-stop — engage the manual kill-switch.
   *  This is a GUARDED system control, never an IG order. */
  activateEmergencyStop: () =>
    apiRequest<{ manualEmergencyStop: boolean }>("/api/system/emergency-stop", {
      method: "POST",
    }),

  /** F3: DELETE /api/system/emergency-stop — clear the manual kill-switch. */
  clearEmergencyStop: () =>
    apiRequest<{ manualEmergencyStop: boolean }>("/api/system/emergency-stop", {
      method: "DELETE",
    }),

  // ── Override « TENIR LE WEEK-END » (owner 2026-10-13 ; sans OTP depuis le 2026-10-26) ──

  /** POST /api/cycles/hold-over-close — sans corps, simple confirmation côté cockpit. 409 pas de cycle / pas de clôture. */
  holdOverClose: () => apiRequest<HoldOverClose>("/api/cycles/hold-over-close", { method: "POST" }),

  /** DELETE /api/cycles/hold-over-close — révoque la tenue (sans OTP : retour au défaut sûr). */
  revokeHoldOverClose: () =>
    apiRequest<{ revoked: boolean }>("/api/cycles/hold-over-close", { method: "DELETE" }),

  /** AUTO mode — GET /api/auto/status. */
  autoStatus: () => apiRequest<AutoStatus>("/api/auto/status"),

  /** POST /api/auto/request-otp — email an OTP for arming AUTO. */
  requestAutoOtp: () =>
    apiRequest<{ otpSent: boolean }>("/api/auto/request-otp", { method: "POST" }),

  /** POST /api/auto/arm — arm AUTO. phrase = "ARM AUTO" + emailed OTP. */
  armAuto: (body: { phrase: string; otp: string }) =>
    apiRequest<AutoStatus>("/api/auto/arm", { method: "POST", body }),

  /** DELETE /api/auto/arm — disarm AUTO (no OTP). */
  disarmAuto: () => apiRequest<AutoStatus>("/api/auto/arm", { method: "DELETE" }),

  /**
   * POST /api/auto/resume — « Reprendre maintenant » (owner 2026-09-01) : lève TOUTE
   * pause AUTO (pertes, attente-gain, flatten) sans OTP ; l'ouvreur relance un BUY au
   * tick suivant via les gates normales. 409 NOT_PAUSED si AUTO n'est pas en pause.
   */
  resumeAuto: () => apiRequest<AutoStatus>("/api/auto/resume", { method: "POST" }),

  // ── Entrée opérateur « Acheter maintenant » (2026-10-22) ──

  /** GET /api/cycles/operator-entry — mode disponible, récapitulatif serveur, dernière demande. */
  operatorEntryStatus: () => apiRequest<OperatorEntryStatus>("/api/cycles/operator-entry"),

  /**
   * POST /api/cycles/operator-entry — cycle actif : 202 { request } (consommée au tick suivant, UN
   * essai) ; hors cycle : lève la pause AUTO puis ouvre un cycle BUY → 201 CycleResponse. 409 sinon
   * (AUTO_NOT_ARMED, REQUEST_PENDING, INITIALE_PENDING, CYCLE_FLAT, TRAIL_ARMED, STREAM_NOT_STARTED,
   * NO_OPEN_WINDOW, CYCLE_START_REFUSED). Sans OTP (décision owner).
   */
  requestOperatorEntry: () =>
    apiRequest<{ request: OperatorEntryRequest } | CycleResponse>("/api/cycles/operator-entry", {
      method: "POST",
    }),

  /** DELETE /api/cycles/operator-entry — annule une demande encore PENDING. */
  cancelOperatorEntry: () =>
    apiRequest<{ cancelled: boolean }>("/api/cycles/operator-entry", { method: "DELETE" }),

  // ── Fermeture opérateur « Fermer cette jambe » (2026-11-02) ──

  /** GET /api/cycles/operator-close — dernière demande de fermeture (toute issue). */
  operatorCloseStatus: () => apiRequest<OperatorCloseStatus>("/api/cycles/operator-close"),

  /**
   * POST /api/cycles/operator-close/{dealId} — 202 { request } (consommée au tick suivant, UN essai).
   * 409 NO_ACTIVE_CYCLE | NOT_OPEN | LAST_LEG | STREAM_NOT_STARTED | REQUEST_PENDING. Sans OTP.
   */
  requestOperatorClose: (dealId: string) =>
    apiRequest<{ request: OperatorCloseRequest }>(`/api/cycles/operator-close/${encodeURIComponent(dealId)}`, {
      method: "POST",
    }),

  /** DELETE /api/cycles/operator-close — annule une demande encore PENDING. */
  cancelOperatorClose: () =>
    apiRequest<{ cancelled: boolean }>("/api/cycles/operator-close", { method: "DELETE" }),

  // ── Dérogation spread CIRCUIT_STREAM_HEALTH (owner 2026-09-02) ──

  streamHealthOverrideStatus: () =>
    apiRequest<StreamHealthOverrideStatus>("/api/breakers/stream-health/status"),

  /** POST /api/breakers/stream-health/override — active la dérogation (JWT seul, sans OTP). */
  activateStreamHealthOverride: () =>
    apiRequest<StreamHealthOverrideStatus>("/api/breakers/stream-health/override", {
      method: "POST",
    }),

  /** DELETE /.../override — annule avant toute ouverture (jamais gaté). */
  cancelStreamHealthOverride: () =>
    apiRequest<StreamHealthOverrideStatus>("/api/breakers/stream-health/override", {
      method: "DELETE",
    }),

  // ── Cockpit MANUEL (dual-cockpit 2026-07-30) — compte séparé du moteur AUTO ──

  manualStatus: () => apiRequest<ManualStatus>("/api/manual/status"),

  /** POST /api/manual/request-otp — email an OTP for arming the MANUAL cockpit. */
  requestManualOtp: () =>
    apiRequest<{ otpSent: boolean }>("/api/manual/request-otp", { method: "POST" }),

  /** POST /api/manual/arm — phrase = "ARM MANUEL" + emailed OTP. */
  armManual: (body: { phrase: string; otp: string }) =>
    apiRequest<ManualStatus>("/api/manual/arm", { method: "POST", body }),

  /** DELETE /api/manual/arm — disarm (no OTP; open positions untouched). */
  disarmManual: () => apiRequest<ManualStatus>("/api/manual/arm", { method: "DELETE" }),

  closeManualPosition: (dealId: string) =>
    apiRequest<ManualPositionView>(`/api/manual/close/${encodeURIComponent(dealId)}`, {
      method: "POST",
    }),

  /** PUT /api/manual/stop/{dealId} — advance-only (§19.5) ; 409 si élargissement. */
  advanceManualStop: (dealId: string, newStop: number) =>
    apiRequest<ManualPositionView>(`/api/manual/stop/${encodeURIComponent(dealId)}`, {
      method: "PUT",
      body: { newStop },
    }),

  /** POST /api/manual/range — mode range ON. Sans OTP : l'automate vit dans la
   *  fenêtre armée que l'OTP a déjà payée. Stop obligatoire (§19). */
  startManualRange: (body: {
    floorLevel: number;
    ceilingLevel: number;
    sizePerPointEur: number;
    stopDistancePoints: number;
  }) => apiRequest<ManualRangeView>("/api/manual/range", { method: "POST", body }),

  /** DELETE /api/manual/range — mode range OFF ; ne clôture jamais rien. */
  stopManualRange: () =>
    apiRequest<ManualRangeView>("/api/manual/range", { method: "DELETE" }),

  // (schema 16) PUT /api/auto/config SUPPRIMÉ — les knobs AUTO s'éditent via
  // les candidats de paramètres versionnés (createParameterCandidate + activation OTP).

  positions: () => apiRequest<PositionResponse[]>("/api/positions"),

  /** GET /api/cycles/{id} — single cycle metadata (read-only). */
  cycleById: (id: number) => apiRequest<CycleResponse>(`/api/cycles/${id}`),

  /** GET /api/cycles/{id}/positions — every position (open + closed)
   *  attached to the cycle, oldest first. Read-only (H2 mirror). */
  cyclePositions: (id: number) =>
    apiRequest<PositionResponse[]>(`/api/cycles/${id}/positions`),

  /** GET /api/cycles/{id}/stop-movements — every stop movement
   *  (accepted + rejected widening attempts), oldest first. */
  cycleStopMovements: (id: number) =>
    apiRequest<StopMovementResponse[]>(`/api/cycles/${id}/stop-movements`),

  historyPage: (page: number, size: number, filters: CycleHistoryFilters = {}) => {
    const qs = buildHistoryQuery({ ...filters, page, size });
    return apiRequest<CycleHistoryPage>(`/api/cycles/history?${qs}`);
  },

  /** URL for the CSV download. Caller hands it to {@code window.open} /
   *  an anchor's {@code href} (browser handles the download with the
   *  current session's cookies + a manually added Authorization). */
  historyCsvUrl: (filters: CycleHistoryFilters = {}) =>
    `/api/cycles/history/csv?${buildHistoryQuery(filters)}`,

  logs: (params: LogsQuery = {}) => {
    const usp = new URLSearchParams();
    if (params.level) usp.set("level", params.level);
    if (params.component) usp.set("component", params.component);
    if (params.search) usp.set("search", params.search);
    if (params.since != null) usp.set("since", String(params.since));
    if (params.limit != null) usp.set("limit", String(params.limit));
    const qs = usp.toString();
    return apiRequest<LogsResponse>(`/api/logs${qs ? `?${qs}` : ""}`);
  },

  logsDownloadUrl: (params: LogsQuery = {}) => {
    const usp = new URLSearchParams();
    if (params.level) usp.set("level", params.level);
    if (params.component) usp.set("component", params.component);
    if (params.search) usp.set("search", params.search);
    return `/api/logs/download${usp.toString() ? `?${usp.toString()}` : ""}`;
  },

  /** GET /api/mode/status — read-only environment descriptor (S56a). */
  modeStatus: () => apiRequest<ModeStatusResponse>("/api/mode/status"),

  // Analytics reads no longer pass `source`: the backend defaults to the running
  // environment (S56a). A deployment only holds its own environment's data, so a
  // DEMO/LIVE selector would be misleading (S56c removed it).
  /** GET /api/analytics/overview?days=N — the Analytics page's single fetch
   *  (2026-07-09 redesign). days=0 → all-time. */
  analyticsOverview: (days = 30) =>
    apiRequest<AnalyticsOverviewResponse>(`/api/analytics/overview?days=${days}`),

  paramHistory: (limit = 50) =>
    apiRequest<ParamChangeRow[]>(`/api/analytics/params?limit=${limit}`),

  decisionQuality: () =>
    apiRequest<DecisionQualityResponse>(`/api/analytics/decision-quality`),

  riskStats: () =>
    apiRequest<RiskStatsResponse>(`/api/analytics/risk-stats`),

  streamQuality: (windowHours = 168) =>
    apiRequest<StreamQualityResponse>(`/api/analytics/stream-quality?windowHours=${windowHours}`),

  /** GET /api/analytics/daily-review[?date=YYYY-MM-DD] — S52 daily P&L review.
   *  Omitting the date returns today (Paris). */
  dailyReview: (date?: string) =>
    apiRequest<DailyReviewResponse>(
      `/api/analytics/daily-review${date ? `?date=${date}` : ""}`,
    ),

  settings: () => apiRequest<SettingsResponse>("/api/settings"),

  /** POST /api/parameters/request-otp — emails a PARAM_APPLY OTP for Promote / Apply. */
  requestParamOtp: () =>
    apiRequest<{ otpSent: boolean }>("/api/parameters/request-otp", { method: "POST" }),

  /** F5: POST /api/parameters/apply — apply a candidate runtime param-set
   *  (DEMO/REPLAY only; LIVE refused backend-side). phrase = "APPLY {hash8}". */
  applyParameters: (body: {
    candidateId: number;
    reason: string;
    phrase: string;
    otp: string;
  }) =>
    apiRequest<ParameterResult>("/api/parameters/apply", {
      method: "POST",
      body,
    }),

  /** F5: POST /api/parameters/rollback — roll back to a previous param-set or
   *  DEFAULT. phrase = "ROLLBACK {hash8|DEFAULT}". */
  rollbackParameters: (body: {
    previousId?: number | null;
    reason: string;
    phrase: string;
    otp: string;
  }) =>
    apiRequest<ParameterResult>("/api/parameters/rollback", {
      method: "POST",
      body,
    }),

  /** Lot 5(8e): POST /api/parameters/candidate — persist a CANDIDATE from edited trading+risk params.
   *  Every field is optional (server starts from the running config, applies only what is sent). Harmless
   *  — nothing applies until the OTP+phrase /apply. Server enforces r_trail ≤ k·SL (422). Returns id+hash. */
  createParameterCandidate: (body: {
    slPoints?: number;
    reinforceThresholdPoints?: number;
    trailDistancePoints?: number;
    maxSide?: number;
    /** Schema 14 — 0 = suit maxSide, sinon 1..maxSide (422 au-delà). */
    trailArmCount?: number;
    /** Schema 14 — renforts autorisés sous trail armé. */
    openAfterArm?: boolean;
    /** Schema 15 (CH10) — TRAIL-STAIR (0 = OFF ; actif seulement si 0 < trail < rt). */
    trailStairAdvancePoints?: number;
    trailStairTrailPoints?: number;
    /** Schema 16 — knobs AUTO versionnés. */
    autoLossThresholdPips?: number;
    autoConsecutiveLossLimit?: number;
    autoNoOpenBeforeCloseMinutes?: number;
    autoResumePullbackPts?: number;
    autoWinPullbackPts?: number;
    cycleRiskPct?: number;
    maxTotalHard?: number;
    consecutiveStopLimit?: number;
    maxSpreadPoints?: number;
    financingEnabled?: boolean;
    financingBaseRatePct?: number;
    flatWhenClosed?: boolean;
    flattenLeadMinutes?: number;
    reason: string;
  }) =>
    apiRequest<{ id: number; paramsHash: string }>("/api/parameters/candidate", {
      method: "POST",
      body,
    }),

  /** GET /api/parameters — read-only version history (lineage + provenance-as-traceability + active flag). */
  listParameters: () => apiRequest<ParameterVersionList>("/api/parameters"),

  /** V20 — GET /api/calendar: weekly schedule + exceptions + derived (open/next close/flatten at). */
  calendar: () => apiRequest<CalendarResponse>("/api/calendar"),

  /** V20 — PUT /api/calendar/weekly (OTP-guarded; OTP via requestParamOtp). */
  putCalendarWeekly: (body: {
    anchorTz: string;
    weekOpenDow: string;
    weekOpenTime: string;
    weekCloseDow: string;
    weekCloseTime: string;
    otp: string;
  }) => apiRequest<{ ok: boolean }>("/api/calendar/weekly", { method: "PUT", body }),

  /** V20 — PUT /api/calendar/exceptions/{date} (upsert, OTP-guarded). */
  putCalendarException: (
    date: string,
    body: {
      type: "CLOSED" | "EARLY_CLOSE" | "MODIFIED";
      openLocal?: string | null;
      closeLocal?: string | null;
      reopenLocal?: string | null;
      label: string;
      note?: string | null;
      otp: string;
    },
  ) => apiRequest<{ ok: boolean }>(`/api/calendar/exceptions/${date}`, { method: "PUT", body }),

  /** V20 — DELETE /api/calendar/exceptions/{date}?otp= (OTP-guarded). */
  deleteCalendarException: (date: string, otp: string) =>
    apiRequest<{ ok: boolean }>(
      `/api/calendar/exceptions/${date}?otp=${encodeURIComponent(otp)}`,
      { method: "DELETE" },
    ),

  /** B2 — GET /api/parameters/{id}: one full version incl. paramsJson (404 → ApiError PARAM_SET_NOT_FOUND). */
  parameterById: (id: number) =>
    apiRequest<ParameterVersionDetail>(`/api/parameters/${id}`),

  /** F4: POST /api/scaling/request-otp — emails a SCALING_CONFIRM OTP. */
  requestScalingOtp: () =>
    apiRequest<{ otpSent: boolean }>("/api/scaling/request-otp", {
      method: "POST",
    }),

  /** F4: POST /api/scaling/confirm — acknowledge a >50% balance jump
   *  (SPEC §19.22). phrase = "SCALE TO {newSize}". */
  confirmScaling: (body: { newSize: number; phrase: string; otp: string }) =>
    apiRequest<{ state: string; scalingReviewCleared: boolean }>(
      "/api/scaling/confirm",
      { method: "POST", body },
    ),

};

/** Build a query-string from history filters; drops empty fields. */
function buildHistoryQuery(
  params: CycleHistoryFilters & { page?: number; size?: number },
): string {
  const usp = new URLSearchParams();
  if (params.page != null) usp.set("page", String(params.page));
  if (params.size != null) usp.set("size", String(params.size));
  if (params.bias) usp.set("bias", params.bias);
  if (params.endReason) usp.set("endReason", params.endReason);
  if (params.pnlSign) usp.set("pnlSign", params.pnlSign);
  if (params.from) usp.set("from", params.from);
  if (params.to) usp.set("to", params.to);
  return usp.toString();
}
